/**
 * Instant Bank Discount (IBD) specific form data builders.
 *
 * Builds guardrails and attributes for INSTANT_BANK_DISCOUNT campaign type
 * from form state. Extracted from formDataBuilder for single-responsibility.
 *
 * @module instantBankDiscountDataBuilder
 */

import { CurrencyCode, EventType, InstrumentType } from 'src/@types/valueMatching/enums';
import { InstantBankDiscountGuardrails } from 'src/@types/valueMatching/guardrails';
import { InstantBankDiscountAttributes } from 'src/@types/valueMatching/attributes';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { parseOptionalNumber } from 'src/utils/helpers';

// ============================================================================
// Public API
// ============================================================================

/** Builds IBD discount guardrails from form state. */
export function buildInstantBankDiscountGuardrails(form: UseCampaignFormResult): InstantBankDiscountGuardrails {
    const guardrails: InstantBankDiscountGuardrails = {};

    const extent = parseOptionalNumber(form.ibdPercentExtentOfCompetitorGuardrail);
    if (extent != null) {
        guardrails.percentExtentOfCompetitorGuardrail = extent;
    }

    const percentAsin = parseOptionalNumber(form.ibdPercentOfASINPriceGuardrail);
    if (percentAsin != null) {
        guardrails.percentOfASINPriceGuardrail = percentAsin;
    }

    const absoluteValue = parseOptionalNumber(form.ibdAbsoluteValueGuardrail);
    if (absoluteValue != null) {
        guardrails.absoluteValueGuardrail = {
            currencyValue: absoluteValue,
            currencyCode: CurrencyCode.INR,
        };
    }

    const maxCapTxn = parseOptionalNumber(form.ibdMaxRewardValuePerTransaction);
    if (maxCapTxn != null) {
        guardrails.maxRewardValuePerTransaction = maxCapTxn;
    }

    const maxCapPromo = parseOptionalNumber(form.ibdMaxRewardValuePerPromotion);
    if (maxCapPromo != null) {
        guardrails.maxRewardValuePerPromotion = maxCapPromo;
    }

    const numTimes = parseOptionalNumber(form.ibdMaxTimesRewardAvailable);
    if (numTimes != null) {
        guardrails.maxTimesRewardAvailable = numTimes;
    }

    return guardrails;
}

/** Builds IBD attributes from form state. */
export function buildInstantBankDiscountAttributes(form: UseCampaignFormResult): InstantBankDiscountAttributes {
    const instantBankDiscountAttributes: InstantBankDiscountAttributes = {};

    // Bank names — extract values from multiselect options
    if (form.ibdBankNames && form.ibdBankNames.length > 0) {
        instantBankDiscountAttributes.bankNames = form.ibdBankNames
            .map((option) => option.value)
            .filter((value): value is string => !!value);
    }

    // Instruments — extract values from multiselect options
    if (form.ibdInstruments && form.ibdInstruments.length > 0) {
        instantBankDiscountAttributes.instruments = form.ibdInstruments
            .map((option) => option.value)
            .filter((value): value is InstrumentType =>
                Object.values(InstrumentType).includes(value as InstrumentType));
    }

    // Event type
    if (form.ibdEventType && Object.values(EventType).includes(form.ibdEventType as EventType)) {
        instantBankDiscountAttributes.eventType = form.ibdEventType as EventType;
    }

    // Terms and conditions
    if (form.ibdTermsAndConditions && form.ibdTermsAndConditions.trim().length > 0) {
        instantBankDiscountAttributes.termsAndConditions = form.ibdTermsAndConditions;
    }

    // Campaign budget
    const budget = parseOptionalNumber(form.ibdCampaignBudget);
    if (budget != null) {
        instantBankDiscountAttributes.campaignBudget = budget;
    }

    return instantBankDiscountAttributes;
}
